import { LegalPageLayout } from '@/components/legal/LegalPageLayout'

export default function PrivacyPolicyPage() {
  return (
    <LegalPageLayout title="Privacy Policy" lastUpdated="November 16, 2025">
      <section>
        <h2>1. Introduction</h2>
        <p>
          Kimel Tech SpA ("we," "our," or "EcoSemantic") operates the EcoSemantic platform at
          ecosemantic.com. This Privacy Policy explains how we collect, use, store, and protect your
          personal information when you use our sustainability intelligence platform.
        </p>
        <p>
          <strong>Contact Information:</strong>
          <br />
          Company: Kimel Tech SpA
          <br />
          Location: Chile
          <br />
          Email: support@ecosemantic.com
        </p>
      </section>

      <section>
        <h2>2. Information We Collect</h2>

        <h3>2.1 Account Information</h3>
        <p>When you create an account, we collect:</p>
        <ul>
          <li>Email address (required)</li>
          <li>Password (stored encrypted)</li>
          <li>Name (optional)</li>
        </ul>

        <h3>2.2 Usage Data</h3>
        <p>We automatically collect:</p>
        <ul>
          <li>Authentication tokens (for security)</li>
          <li>Subscription tier information</li>
          <li>Tool call statistics</li>
          <li>Chat conversation history (when using our AI assistant)</li>
          <li>Transaction history (via Paddle for payments)</li>
        </ul>

        <h3>2.3 Technical Data</h3>
        <ul>
          <li>Theme preference (dark/light mode) if you consent</li>
          <li>Browser type and version (standard web logs)</li>
          <li>IP address (for security and authentication)</li>
        </ul>

        <h3>2.4 Payment Information</h3>
        <p>
          We use Paddle.com as our payment processor. Paddle collects and processes billing
          information, payment card details, and transaction records.{' '}
          <strong>We do not store payment card details</strong> — these are handled entirely by
          Paddle.
        </p>
      </section>

      <section>
        <h2>3. Legal Basis for Processing (GDPR)</h2>
        <p>We process your data based on:</p>
        <ul>
          <li>
            <strong>Contract Performance</strong> (Article 6(1)(b)): To provide our SaaS platform
            services
          </li>
          <li>
            <strong>Legitimate Interest</strong> (Article 6(1)(f)): For platform security, fraud
            prevention, and service improvement
          </li>
          <li>
            <strong>Consent</strong> (Article 6(1)(a)): For optional features like theme preferences
          </li>
          <li>
            <strong>Legal Obligation</strong> (Article 6(1)(c)): For tax and accounting records
          </li>
        </ul>
      </section>

      <section>
        <h2>4. How We Use Your Information</h2>
        <p>We use your data to:</p>
        <ul>
          <li>Provide access to the EcoSemantic platform</li>
          <li>Process your subscription and payments</li>
          <li>Deliver AI-powered sustainability analysis via MCP tools</li>
          <li>Send essential service communications (password resets, subscription updates)</li>
          <li>Maintain platform security</li>
          <li>Comply with legal obligations</li>
        </ul>

        <p>
          <strong>We do NOT:</strong>
        </p>
        <ul>
          <li>Sell your personal data to third parties</li>
          <li>Use your data for advertising or marketing purposes</li>
          <li>Track you across other websites</li>
          <li>Use analytics or marketing cookies</li>
        </ul>
      </section>

      <section>
        <h2>5. Data Sharing and Third Parties</h2>
        <p>We share data only with essential service providers:</p>

        <h3>5.1 Payment Processing</h3>
        <ul>
          <li>
            <strong>Paddle.com</strong> - Payment processing, subscription management, VAT/tax
            handling
          </li>
          <li>
            Privacy Policy:{' '}
            <a
              href="https://www.paddle.com/legal/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              paddle.com/legal/privacy
            </a>
          </li>
        </ul>

        <h3>5.2 AI Model Providers</h3>
        <ul>
          <li>
            <strong>OpenRouter.ai</strong> - AI model routing and processing for chat functionality
          </li>
          <li>
            Privacy Policy:{' '}
            <a href="https://openrouter.ai/privacy" target="_blank" rel="noopener noreferrer">
              openrouter.ai/privacy
            </a>
          </li>
          <li>
            <strong>Anthropic Claude API</strong> (via OpenRouter) - AI model for chat responses
          </li>
          <li>
            Privacy Policy:{' '}
            <a
              href="https://www.anthropic.com/legal/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              anthropic.com/legal/privacy
            </a>
          </li>
        </ul>

        <h3>5.3 Infrastructure Providers</h3>
        <ul>
          <li>
            <strong>Hosting Services</strong> - Server infrastructure for our platform
          </li>
        </ul>

        <p>
          <strong>No other third parties</strong> have access to your data.
        </p>
      </section>

      <section>
        <h2>6. Data Retention</h2>

        <h3>6.1 Active Accounts</h3>
        <p>Data retained while your account is active.</p>

        <h3>6.2 Account Deletion</h3>
        <div className="not-prose bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-md p-4 mb-6">
          <p className="font-bold text-yellow-800 dark:text-yellow-200 mb-2">
            ⚠️ IMPORTANT: Export your data BEFORE deleting your account.
          </p>
          <p className="text-yellow-700 dark:text-yellow-300 text-sm">
            After deletion, you cannot access your chat history or account data. Use the "Download
            My Data" feature in Account Settings first.
          </p>
        </div>

        <p>When you delete your account:</p>

        <p>
          <strong>Immediately Anonymized:</strong>
        </p>
        <ul>
          <li>Email address (replaced with anonymized identifier)</li>
          <li>Name (replaced with "Deleted User")</li>
          <li>Password (randomized)</li>
          <li>All API tokens (revoked)</li>
        </ul>

        <p>
          <strong>Retained for Legal/Business Reasons:</strong>
        </p>
        <ul>
          <li>Transaction records (7 years - tax law requirement)</li>
          <li>Subscription history (7 years - financial compliance)</li>
          <li>Deletion audit log (email stored for account recovery verification)</li>
          <li>
            Chat conversation history (anonymized, inaccessible to user, retained for analytics and
            service improvement)
          </li>
        </ul>

        <p>
          <strong>Why We Retain Some Data:</strong>
        </p>
        <ul>
          <li>
            <strong>Legal Obligation</strong> (GDPR Article 6(1)(c)): Tax and accounting laws
            require 7-10 year retention
          </li>
          <li>
            <strong>Legitimate Interest</strong> (GDPR Article 6(1)(f)): Fraud prevention and
            subscription management
          </li>
          <li>
            <strong>Contract Performance</strong> (GDPR Article 6(1)(b)): Managing active
            subscriptions that haven't expired
          </li>
        </ul>
      </section>

      <section>
        <h2>7. Your Rights (GDPR/Data Protection)</h2>

        <h3>7.1 Right to Access (Article 15)</h3>
        <p>
          <strong>Get a copy of all your personal data</strong>
        </p>
        <p>How to exercise:</p>
        <ol>
          <li>Log into your account</li>
          <li>Go to Account Settings</li>
          <li>Click "Download My Data"</li>
          <li>Download JSON file with all your data</li>
        </ol>
        <p>Response time: Immediate (automated export)</p>

        <h3>7.2 Right to Rectification (Article 16)</h3>
        <p>
          <strong>Correct inaccurate or incomplete data</strong>
        </p>
        <p>
          Update profile information directly in Account Settings or contact support@ecosemantic.com
        </p>

        <h3>7.3 Right to Erasure / "Right to be Forgotten" (Article 17)</h3>
        <p>
          <strong>Request deletion of your personal data</strong>
        </p>
        <p>How to exercise:</p>
        <ol>
          <li>Log into your account</li>
          <li>Go to Account Settings</li>
          <li>Click "Delete Account"</li>
          <li>Confirm deletion</li>
        </ol>
        <p>
          <strong>Important:</strong> Account deletion is permanent and irreversible. Financial
          records must be retained for tax compliance.
        </p>

        <h3>7.4 Right to Data Portability (Article 20)</h3>
        <p>
          <strong>Receive your data in machine-readable format</strong>
        </p>
        <p>Same as Right to Access (Section 7.1) - export data as JSON file.</p>

        <h3>7.5 Right to Object (Article 21)</h3>
        <p>Object to optional processing through cookie banner or contact support.</p>

        <h3>7.6 Right to Withdraw Consent</h3>
        <p>Withdraw consent for optional features anytime through settings.</p>

        <p>
          <strong>For assistance:</strong> Email support@ecosemantic.com with subject "GDPR Request"
        </p>
        <p>We will respond within 30 days as required by GDPR.</p>
      </section>

      <section>
        <h2>8. Data Security</h2>
        <p>We implement appropriate technical and organizational measures:</p>
        <ul>
          <li>Password encryption (hashed, never stored in plain text)</li>
          <li>HTTPS encryption for all data transmission</li>
          <li>Access controls and authentication requirements</li>
          <li>Regular security updates and monitoring</li>
          <li>Database backups for data recovery</li>
        </ul>
        <p>
          <strong>However</strong>, no method of transmission over the internet is 100% secure. We
          cannot guarantee absolute security.
        </p>
      </section>

      <section>
        <h2>9. International Data Transfers</h2>
        <p>EcoSemantic is operated from Chile. If you are located in the EU/EEA:</p>
        <ul>
          <li>Your data may be transferred to and processed in Chile</li>
          <li>
            We rely on Standard Contractual Clauses (SCCs) or adequacy decisions where applicable
          </li>
          <li>
            Third-party processors (Paddle, Anthropic) have their own data transfer mechanisms
          </li>
        </ul>
      </section>

      <section>
        <h2>10. Cookies and Tracking</h2>
        <p>We use minimal cookies:</p>

        <h3>Essential Cookies (No consent required):</h3>
        <ul>
          <li>Authentication tokens</li>
          <li>Session management</li>
          <li>Security features</li>
        </ul>

        <h3>Functional Cookies (Requires consent):</h3>
        <ul>
          <li>Theme preference (dark/light mode)</li>
        </ul>

        <p>
          <strong>We do NOT use:</strong>
        </p>
        <ul>
          <li>Analytics cookies</li>
          <li>Marketing cookies</li>
          <li>Cross-site tracking</li>
          <li>Third-party advertising cookies</li>
        </ul>
      </section>

      <section>
        <h2>11. Children's Privacy</h2>
        <p>
          EcoSemantic is not intended for users under 16 years of age. We do not knowingly collect
          data from children under 16. If you believe we have inadvertently collected such data,
          contact us immediately.
        </p>
      </section>

      <section>
        <h2>12. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy. Changes will be posted on this page with an updated
          "Last Updated" date. Significant changes will be communicated via email.
        </p>
      </section>

      <section>
        <h2>13. Contact Us</h2>
        <p>For privacy questions or to exercise your rights:</p>
        <ul>
          <li>
            <strong>Email:</strong> support@ecosemantic.com
          </li>
          <li>
            <strong>Company:</strong> Kimel Tech SpA, Chile
          </li>
          <li>
            <strong>Response Time:</strong> Within 30 days
          </li>
        </ul>
      </section>

      <section>
        <h2>14. Supervisory Authority</h2>
        <p>
          If you're in the EU/EEA and believe we've violated GDPR, you have the right to lodge a
          complaint with your local data protection authority.
        </p>
      </section>
    </LegalPageLayout>
  )
}
